import { useState, useEffect } from 'react'
import { Header } from '../components/layout/Header'

const API_BASE = '/api'

interface SLADefinition {
  id: string
  name: string
  description: string
  target_percentage: number
  measurement_period: string
  penalty_structure: string
  applies_to: string
  active: boolean
}

interface SLAMetric {
  id: string
  sla_id: string
  customer_name: string
  target: number
  actual: number
  status: string
  downtime_minutes?: number
  incidents?: number
  credit_amount?: number
}

interface Incident {
  id: string
  title: string
  severity: string
  started_at: string
  resolved_at: string | null
  duration_minutes: number
  affected_services: string[]
  root_cause: string
  sla_impact: boolean
}

export default function SLAMonitoringPage() {
  const [definitions, setDefinitions] = useState<SLADefinition[]>([])
  const [metrics, setMetrics] = useState<SLAMetric[]>([])
  const [incidents, setIncidents] = useState<Incident[]>([])
  const [statistics, setStatistics] = useState<any>(null)
  const [realtimeMetrics, setRealtimeMetrics] = useState<any>(null)

  useEffect(() => {
    fetchData()
    const interval = setInterval(fetchRealtime, 5000)
    return () => clearInterval(interval)
  }, [])

  const fetchData = async () => {
    const [defRes, metRes, incRes, statsRes] = await Promise.all([
      fetch(API_BASE + '/sla/definitions'),
      fetch(API_BASE + '/sla/metrics'),
      fetch(API_BASE + '/sla/incidents'),
      fetch(API_BASE + '/sla/statistics')
    ])
    if (defRes.ok) setDefinitions((await defRes.json()).definitions || [])
    if (metRes.ok) setMetrics((await metRes.json()).metrics || [])
    if (incRes.ok) setIncidents((await incRes.json()).incidents || [])
    if (statsRes.ok) setStatistics(await statsRes.json())
    fetchRealtime()
  }

  const fetchRealtime = async () => {
    const res = await fetch(API_BASE + '/sla/realtime')
    if (res.ok) setRealtimeMetrics(await res.json())
  }

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'met': return 'bg-green-100 text-green-800'
      case 'breached': return 'bg-red-100 text-red-800'
      case 'at_risk': return 'bg-yellow-100 text-yellow-800'
      default: return 'bg-gray-100 text-gray-800'
    }
  }

  const getSeverityColor = (severity: string) => {
    switch (severity) {
      case 'critical': return 'bg-red-100 text-red-800'
      case 'major': return 'bg-orange-100 text-orange-800'
      case 'moderate': return 'bg-yellow-100 text-yellow-800'
      case 'minor': return 'bg-blue-100 text-blue-800'
      default: return 'bg-gray-100 text-gray-800'
    }
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      <main className="max-w-7xl mx-auto px-4 py-8">
        <h1 className="text-3xl font-bold mb-8">SLA Monitoring</h1>

        {statistics && (
          <div className="grid grid-cols-6 gap-4 mb-8">
            <div className={`bg-white rounded-lg shadow p-4 border-l-4 ${statistics.compliance_rate >= 95 ? 'border-green-500' : 'border-red-500'}`}>
              <div className="text-sm text-gray-500">Compliance Rate</div>
              <div className={`text-2xl font-bold ${statistics.compliance_rate >= 95 ? 'text-green-600' : 'text-red-600'}`}>{statistics.compliance_rate}%</div>
            </div>
            <div className="bg-white rounded-lg shadow p-4">
              <div className="text-sm text-gray-500">Current Uptime</div>
              <div className="text-2xl font-bold text-green-600">{statistics.current_uptime}%</div>
            </div>
            <div className="bg-white rounded-lg shadow p-4">
              <div className="text-sm text-gray-500">SLAs Met</div>
              <div className="text-2xl font-bold">{statistics.slas_met}/{statistics.slas_met + statistics.slas_breached}</div>
            </div>
            <div className="bg-white rounded-lg shadow p-4">
              <div className="text-sm text-gray-500">Breached</div>
              <div className="text-2xl font-bold text-red-600">{statistics.slas_breached}</div>
            </div>
            <div className="bg-white rounded-lg shadow p-4">
              <div className="text-sm text-gray-500">Credits Issued</div>
              <div className="text-2xl font-bold text-yellow-600">${statistics.total_credits_issued}</div>
            </div>
            <div className="bg-white rounded-lg shadow p-4">
              <div className="text-sm text-gray-500">Avg Resolution</div>
              <div className="text-2xl font-bold">{statistics.avg_resolution_time}m</div>
            </div>
          </div>
        )}

        <div className="grid grid-cols-3 gap-6 mb-6">
          <div className="col-span-2 space-y-6">
            <div className="bg-white rounded-lg shadow">
              <div className="px-6 py-4 border-b"><h2 className="text-xl font-semibold">SLA Performance</h2></div>
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Customer</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Target</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actual</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Downtime</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {metrics.map(m => (
                    <tr key={m.id}>
                      <td className="px-6 py-4 font-medium">{m.customer_name}</td>
                      <td className="px-6 py-4">{m.target}%</td>
                      <td className="px-6 py-4 font-semibold">{m.actual}%</td>
                      <td className="px-6 py-4"><span className={`px-2 py-1 rounded-full text-xs ${getStatusColor(m.status)}`}>{m.status}</span></td>
                      <td className="px-6 py-4 text-sm">{m.downtime_minutes ? `${m.downtime_minutes}m` : '-'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="bg-white rounded-lg shadow">
              <div className="px-6 py-4 border-b"><h2 className="text-xl font-semibold">Recent Incidents</h2></div>
              <div className="divide-y">
                {incidents.map(inc => (
                  <div key={inc.id} className="p-4">
                    <div className="flex justify-between items-start mb-2">
                      <div>
                        <div className="font-semibold">{inc.title}</div>
                        <div className="text-sm text-gray-500">{inc.affected_services.join(', ')}</div>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className={`px-2 py-1 rounded text-xs ${getSeverityColor(inc.severity)}`}>{inc.severity}</span>
                        {inc.sla_impact && <span className="text-red-600 text-xs">SLA Impact</span>}
                      </div>
                    </div>
                    <div className="text-sm text-gray-600">{inc.root_cause}</div>
                    <div className="flex justify-between mt-2 text-xs text-gray-400">
                      <span>Duration: {inc.duration_minutes}m</span>
                      <span>{new Date(inc.started_at).toLocaleString()}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className="space-y-6">
            {realtimeMetrics && (
              <div className="bg-white rounded-lg shadow p-6">
                <h3 className="text-lg font-semibold mb-4">Live Metrics</h3>
                <div className="space-y-4">
                  <div>
                    <div className="flex justify-between text-sm mb-1">
                      <span>Uptime</span>
                      <span className="text-green-600 font-semibold">{realtimeMetrics.uptime_percentage?.toFixed(3)}%</span>
                    </div>
                    <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                      <div className="h-full bg-green-500 rounded-full" style={{ width: `${Math.min(realtimeMetrics.uptime_percentage, 100)}%` }}></div>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4 pt-2">
                    <div className="text-center p-2 bg-gray-50 rounded">
                      <div className="text-xl font-bold">{realtimeMetrics.api_response_time_ms}ms</div>
                      <div className="text-xs text-gray-500">Response Time</div>
                    </div>
                    <div className="text-center p-2 bg-gray-50 rounded">
                      <div className="text-xl font-bold">{realtimeMetrics.requests_per_second}</div>
                      <div className="text-xs text-gray-500">Req/sec</div>
                    </div>
                    <div className="text-center p-2 bg-gray-50 rounded">
                      <div className="text-xl font-bold">{realtimeMetrics.error_rate}%</div>
                      <div className="text-xs text-gray-500">Error Rate</div>
                    </div>
                    <div className="text-center p-2 bg-gray-50 rounded">
                      <div className="text-xl font-bold">{realtimeMetrics.active_connections}</div>
                      <div className="text-xs text-gray-500">Connections</div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            <div className="bg-white rounded-lg shadow p-6">
              <h3 className="text-lg font-semibold mb-4">SLA Definitions</h3>
              <div className="space-y-3">
                {definitions.filter(d => d.active).map(def => (
                  <div key={def.id} className="p-3 bg-gray-50 rounded">
                    <div className="font-medium text-sm">{def.name}</div>
                    <div className="text-xs text-gray-500 mt-1">{def.description}</div>
                    <div className="flex justify-between mt-2 text-xs">
                      <span className="text-gray-500">Target: {def.target_percentage}%</span>
                      <span className="text-gray-500 capitalize">{def.applies_to}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  )
}
